import { shopifyComponentCopy } from '@testfeed/product-language'
import { Progress } from '../../app-home/art/Art'
import type { TestsIndexRecord } from '../types'
import { StatusIssueNote } from './StatusIssueNote'
import { RunMilestones, stageLabel } from './RunMilestones'

/** Valid aggregate responses as a bar and count; otherwise a plain note, except while waiting to start. */
function ResponseProgress({ record }: { record: TestsIndexRecord }) {
  const copy = shopifyComponentCopy.testsIndex
  const { progress, stage } = record
  if (!progress) {
    // A queued run already says it is waiting; "progress isn't available yet" would only repeat that.
    if (stage === 'waiting') return null
    return <s-text color="subdued">{copy.progressUnavailable}</s-text>
  }
  return (
    <>
      <Progress value={progress.percent} label={copy.progressAccessibilityLabel.replace('{title}', record.title)} />
      <s-text color="subdued">
        {copy.progressResponses
          .replace('{completed}', String(progress.completedResponses))
          .replace('{planned}', String(progress.plannedResponses))}
      </s-text>
    </>
  )
}

/**
 * A queued or running test. Shows the real run status; the server-confirmed milestone (`latestRun.stage`) as a
 * checklist when known; and, only when the status response supplies valid completed/planned responses, a count and
 * percentage. No time estimates, cancel, or restart.
 */
export function ActiveRunCard({
  record,
  onOpen,
  onRetryStatus,
}: {
  record: TestsIndexRecord
  onOpen: () => void
  onRetryStatus: () => void
}) {
  const copy = shopifyComponentCopy.testsIndex
  const { progress, stage } = record
  const currentStageLabel = stage ? stageLabel(stage) : null
  return (
    <s-box padding="base" border="base" borderRadius="base">
      <s-grid gap="base">
        <s-stack direction="inline" gap="small-200" alignItems="center">
          <s-badge tone="info" icon="clock">
            {record.statusLabel}
          </s-badge>
          {record.timeLabel && <s-text color="subdued">{record.timeLabel}</s-text>}
        </s-stack>

        <s-grid gridTemplateColumns="1fr auto" alignItems="baseline" gap="small">
          <s-heading>{record.title}</s-heading>
          {progress && <s-text type="strong">{progress.percent}%</s-text>}
        </s-grid>

        <s-grid gap="small-200">
          {currentStageLabel && <s-text color="subdued">{currentStageLabel}</s-text>}
          <ResponseProgress record={record} />
        </s-grid>

        {stage && <RunMilestones stage={stage} />}

        {record.statusIssue && <StatusIssueNote issue={record.statusIssue} onRetry={onRetryStatus} />}

        <s-stack direction="inline" gap="base" alignItems="center">
          <s-button variant="secondary" onClick={onOpen}>
            {copy.viewProgressAction}
          </s-button>
          <s-text color="subdued">{copy.leavePageNote}</s-text>
        </s-stack>
      </s-grid>
    </s-box>
  )
}
